import DOMPurify from 'dompurify';

// Render seguro del cuerpo HTML de emails ingeridos (comentarios con
// cuerpo_html). El backend lo sanea en servidor, pero aquí se trata igualmente
// como NO confiable (defensa en profundidad) y se aplica el contrato acordado:
//   - Imágenes inline llegan como <img src="adjunto:<id>">: se resuelven contra
//     los adjuntos DEL PROPIO ticket (la ruta es una URL firmada fresca del GET
//     de adjuntos, caduca en 1 h — nunca cachear el resultado). Un id que no
//     esté en la lista del ticket se elimina: un remitente puede forjar
//     adjunto:99999 y jamás debe resolverse contra nada global.
//   - Tras la sustitución solo se renderizan <img> con src https:.
//   - El HTML no trae recorte de firma/cita: los bloques citados conocidos
//     (Gmail/Apple Mail/Outlook) se marcan con la clase tickets-html__quote
//     para colapsarlos por CSS ("mostrar contenido citado").

// OJO: DOMPurify valida con esta regex el valor de TODOS los atributos, no solo
// los de URI. Por eso, además de los esquemas permitidos (https?, mailto, tel y
// el adjunto: del contrato, que se resuelve después), debe aceptar valores que
// no parecen URI (ramas `[^a-z]` y `[a-z+.\-]+…` del patrón por defecto: cosas
// como "_blank", "cite" o "noopener"). data:, cid:, javascript:, etc. no casan
// con ninguna rama y quedan fuera.
const URI_PERMITIDAS = /^(?:(?:https?|mailto|tel|adjunto):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i;

// srcset/sizes permitirían colar URLs de imagen esquivando la regla del src.
const CONFIG_DOMPURIFY = {
  FORBID_TAGS: [
    'script',
    'style',
    'iframe',
    'object',
    'embed',
    'form',
    'input',
    'button',
    'link',
    'meta',
    'base',
  ],
  FORBID_ATTR: ['srcset', 'sizes', 'formaction'],
  // type: DOMPurify lo recorta por defecto y se necesita para detectar la cita
  // de Apple Mail (blockquote[type="cite"]); es inerte con form/input prohibidos.
  ADD_ATTR: ['target', 'type'],
  ALLOWED_URI_REGEXP: URI_PERMITIDAS,
};

// Contenedores de respuesta citada que insertan los clientes de correo.
const SELECTOR_CITAS = '.gmail_quote, blockquote[type="cite"], #divRplyFwdMsg';

export const CLASE_CITA = 'tickets-html__quote';

// Devuelve { html, tieneCitado }. html es '' si no hay nada renderizable.
export const sanitizarHtmlEmail = (html, adjuntos = []) => {
  if (!html || typeof html !== 'string') return { html: '', tieneCitado: false };

  const rutaPorId = new Map();
  adjuntos.forEach((a) => {
    if (a && a.id != null && /^https:/i.test(String(a.ruta || ''))) {
      rutaPorId.set(String(a.id), String(a.ruta));
    }
  });

  const limpio = DOMPurify.sanitize(html, CONFIG_DOMPURIFY);

  // Post-proceso sobre DOM inerte (template no ejecuta scripts ni carga
  // recursos al parsear). Nunca sobre strings: nada de interpolar en templates.
  const contenedor = document.createElement('template');
  contenedor.innerHTML = limpio;

  contenedor.content.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src') || '';
    const inline = src.match(/^adjunto:(.+)$/i);
    if (inline) {
      const ruta = rutaPorId.get(inline[1]);
      if (!ruta) {
        img.remove();
        return;
      }
      img.setAttribute('src', ruta);
    } else if (!/^https:/i.test(src)) {
      img.remove();
      return;
    }
    img.setAttribute('loading', 'lazy');
    img.setAttribute('referrerpolicy', 'no-referrer');
  });

  // El backend ya manda target/rel en los <a>; se refuerza por si el saneo o
  // un email manual los pierde.
  contenedor.content.querySelectorAll('a[href]').forEach((a) => {
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer nofollow');
  });

  const citas = contenedor.content.querySelectorAll(SELECTOR_CITAS);
  citas.forEach((cita) => cita.classList.add(CLASE_CITA));

  return { html: contenedor.innerHTML, tieneCitado: citas.length > 0 };
};

export default sanitizarHtmlEmail;
